import React, { MouseEvent } from 'react';
import { Row, Table, Col } from 'react-bootstrap';
import { useSortableTable } from './useSortTable';
import SmashTableAction, { TableActionItem } from './SmashTableAction';
import SmashTableHead, { TableHeaders } from './SmashTableHead';
import './SmashTable.scss';
import SmashSpinner from '../SmashSpinner/SmashSpinner';
import { formatDate, humanizeBytes } from '../../shared/helpers/helpers';
import { useNavigate } from 'react-router-dom';

type PrimitiveType = string | number | boolean | Symbol;

function isPrimitive(value: any): value is PrimitiveType {
    return (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean' ||
        typeof value === 'symbol'
    );
}

interface MinTableItem { }

type CustomRenderers<T extends MinTableItem> = Partial<Record<keyof T, (it: T) => React.ReactNode>>;

interface TableProps<T extends MinTableItem> {
    items: T[];
    loading: boolean;
    hasError?: boolean;
    headers: TableHeaders[];
    onRowClick?: string;
    customRenderers?: CustomRenderers<T>;
    tableActions?: TableActionItem[];
    textFallback?: string;
}

const SmashTable = <Item extends MinTableItem>(props: TableProps<Item>) => {
    const [tableData, handleSorting] = useSortableTable(props.items, props.headers);
    
    const navigate = useNavigate();

    const handleRowClick = (event: MouseEvent, item:any) => {
        event.preventDefault();
        event.stopPropagation();

        if(props.onRowClick) {
            navigate( item[props.onRowClick] );
        }
    }
    function renderRow(item: Item, idx: number) {
        return (
            <tr key={idx}>
                {props.headers.map((col, cellId) => {
                    const customrenderer = props.customRenderers?.[col.accessor as keyof Item];
                    if (customrenderer) {
                        return <td key={cellId} onClick={(e) => handleRowClick(e, item)} className={ props.onRowClick ? 'clickable': ''}>{customrenderer(item as Item)}</td>;
                    }

                    if(col.type === 'date' ) {
                        return (<td key={cellId} onClick={(e) => handleRowClick(e, item)} className={ props.onRowClick ? 'clickable': ''}>{formatDate(item[col.accessor as keyof Item] as string)}</td>)
                    }

                    if(col.type === 'bytes' ) {
                        return (<td key={cellId} onClick={(e) => handleRowClick(e, item)} className={ props.onRowClick ? 'clickable': ''}>{humanizeBytes(item[col.accessor as keyof Item] as number)}</td>)
                    }

                    return (
                        <td key={cellId} onClick={(e) => handleRowClick(e, item)} className={ 'd-block d-lg-table-cell' && props.onRowClick ? 'clickable ': ''}><>{isPrimitive(item[col.accessor as keyof Item]) ? item[col.accessor as keyof Item] : ""}</></td>
                    );
                })}
                {props.tableActions?.length &&
                    <td className="d-block d-lg-table-cell"><SmashTableAction item={item} tableActions={props.tableActions} /></td>
                }
            </tr>
        )
    }



    if (props.loading) {
        return (<div className="d-flex justify-content-center align-items-center spinner-container">
            <SmashSpinner></SmashSpinner>
        </div>)
    }

    if (!tableData.length) {
        return (<Row>
            <Col className="text-center">
                {props?.textFallback ? props.textFallback : 'No Data'}
            </Col>
        </Row>)
    }

    if (props.hasError) {
        return (
            <div>An error has occurred</div>
        )
    }

    return (
        <>
            <Table hover >
                <SmashTableHead columns={props.headers} handleSorting={handleSorting} actions={props.tableActions} />
                <tbody>
                    {tableData.length && tableData.map((item: Item, idx: number) => renderRow(item, idx))}
                </tbody>
            </Table>
       </>)
}

export default SmashTable;